<template>
  <Teleport to="body">
    <Transition name="modal">
      <div v-if="isOpen" class="fixed inset-0 z-[120] overflow-y-auto" aria-labelledby="modal-title" role="dialog"
        aria-modal="true">
        <!-- Background overlay -->
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity z-[115]" @click="closeModal"></div>

        <!-- Modal panel -->
        <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0 relative z-[120]">
          <div :class="modalClasses">
            <!-- Modal header -->
            <div :class="`bg-white ${props.noBodyPadding ? '' : 'px-4 pb-4 pt-5 sm:p-6 sm:pb-4'} ${props.headerClass}`">
              <div :class="`flex items-center justify-between ${props.noHeaderBorder ? '' : 'mb-4'}`">
                <h3 v-if="title" class="text-lg font-medium leading-6 text-gray-900" id="modal-title">
                  {{ title }}
                </h3>
                <button type="button"
                  class="rounded-md bg-white text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                  @click="closeModal">
                  <span class="sr-only">Close</span>
                  <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <!-- Modal content -->
              <div class="mt-3 text-center sm:mt-0 sm:text-left">
                <slot name="body">
                  <slot></slot>
                </slot>
              </div>
            </div>

            <!-- Modal footer -->
            <div v-if="$slots.footer"
              :class="`${props.noFooterBorder ? '' : 'bg-gray-50'} ${props.footerClass || 'px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6'}`">
              <slot name="footer"></slot>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
/* eslint-env node */
/* eslint-disable no-undef */
/**
 * Reusable Modal Component
 *
 * @component BaseModal
 * @description A flexible modal component using Vue 3 Teleport and Transition
 * @example
 * <BaseModal v-model:is-open="showModal" title="My Modal">
 *   <p>Modal content here</p>
 *   <template #footer>
 *     <button @click="showModal = false">Close</button>
 *   </template>
 * </BaseModal>
 */

// Props
const props = defineProps({
  /**
   * Controls modal visibility
   */
  isOpen: {
    type: Boolean,
    default: false
  },
  /**
   * Modal title displayed in header
   */
  title: {
    type: String,
    default: ''
  },
  /**
   * Whether clicking overlay closes modal
   */
  closeOnOverlay: {
    type: Boolean,
    default: true
  },
  /**
   * Maximum width of the modal
   */
  maxWidth: {
    type: String,
    default: '4xl'
  },
  /**
   * Additional CSS classes for modal container
   */
  modalClass: {
    type: String,
    default: ''
  },
  /**
   * Additional CSS classes for header
   */
  headerClass: {
    type: String,
    default: ''
  },
  /**
   * Additional CSS classes for footer
   */
  footerClass: {
    type: String,
    default: ''
  },
  /**
   * Remove header border
   */
  noHeaderBorder: {
    type: Boolean,
    default: false
  },
  /**
   * Remove footer border
   */
  noFooterBorder: {
    type: Boolean,
    default: false
  },
  /**
   * Remove default body padding
   */
  noBodyPadding: {
    type: Boolean,
    default: false
  }
})

// Emits - include all possible events that might be passed
const emit = defineEmits([
  'update:isOpen',
  'close',
  'authRedirect',
  'continueWithoutSaving'
])

/**
 * Close modal handler
 */
const closeModal = () => {
  if (props.closeOnOverlay) {
    emit('update:isOpen', false)
    emit('close')
  }
}

// Keyboard accessibility
import { computed, onMounted, onUnmounted } from 'vue'

// Keyboard accessibility

// Computed property for modal classes with predefined width mappings
const modalClasses = computed(() => {
  const baseClasses = 'relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full'

  // Map maxWidth props to actual Tailwind classes
  const widthMapping = {
    'xs': 'sm:max-w-xs',
    'sm': 'sm:max-w-sm',
    'md': 'sm:max-w-md',
    'lg': 'sm:max-w-lg',
    'xl': 'sm:max-w-xl',
    '2xl': 'sm:max-w-2xl',
    '3xl': 'sm:max-w-3xl',
    '4xl': 'sm:max-w-4xl',
    '5xl': 'sm:max-w-5xl',
    '6xl': 'sm:max-w-6xl',
    '7xl': 'sm:max-w-7xl'
  }

  const maxWidthClass = widthMapping[props.maxWidth] || 'sm:max-w-4xl'
  return `${baseClasses} ${maxWidthClass} ${props.modalClass}`.trim()
})

const handleEscape = (event) => {
  if (event.key === 'Escape' && props.isOpen) {
    closeModal()
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleEscape)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleEscape)
})
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.25s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .transform,
.modal-leave-active .transform {
  transition: transform 0.25s ease;
}

.modal-enter-from .transform,
.modal-leave-to .transform {
  transform: translate(0, 4rem) scale(0.95);
}
</style>
